import React, { useState } from 'react';
import {
  Box, VStack, HStack, Text, Button, Icon, Badge, Tabs, TabList, Tab,
  TabPanels, TabPanel, useToast, Divider, useColorModeValue, Spinner,
} from '@chakra-ui/react';
import { FiUploadCloud, FiRefreshCw, FiExternalLink, FiArrowRight } from 'react-icons/fi';
import AriveSyncBadge from '../../../components/Arive/AriveSyncBadge';
import AriveDeepLink from '../../../components/Arive/AriveDeepLink';
import AriveLoanSummary from '../../../components/Arive/AriveLoanSummary';
import AriveKeyDatesCard from './AriveKeyDatesCard';
import AriveTrackersCard from './AriveTrackersCard';
import AriveLoanTeamCard from './AriveLoanTeamCard';
import AriveCreditCard from './AriveCreditCard';
import AriveFinancialCard from './AriveFinancialCard';
import { useAriveLoanDetails, useAriveSyncStatus, useArivePush, useAriveCredentials, useAriveAccess } from '../../../hooks/useArive';
import { getAriveStageLabel } from '../../../constants/ariveConstants';
import { useAuth } from '../../../contexts/AuthContext';

export default function AriveSyncPanel({ deal, onDealUpdate }) {
  // All hooks must be called unconditionally before any early return
  // (see .cursor/rules/react-hooks-no-conditional-calls.mdc).
  const { currentUser } = useAuth();
  const { hasCredentials, isEnabled, loading: credsLoading } = useAriveCredentials(currentUser?.uid);
  const { companySyncConfigured, loading: accessLoading } = useAriveAccess();
  const toast = useToast();
  const { status: syncStatus, lastCheckedAt, refetch: refetchFreshness } = useAriveSyncStatus(deal);
  const { loanData, loading: refreshing, refresh } = useAriveLoanDetails(deal);
  const { pushToArive, pushing } = useArivePush();
  const [tabIndex, setTabIndex] = useState(0);

  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const bg = useColorModeValue('white', 'gray.800');

  const isLinked = !!deal?.ariveId;
  const isLead = deal?.ariveEntityType === 'lead';
  const isLoan = deal?.ariveEntityType === 'loan';
  // Phase A: include legacy `lead-qualified` so deals still in that stage continue to show the lead UI.
  const LEAD_STAGES = ['lead-new', 'lead-contacted', 'lead-quoted', 'lead-qualified', 'lead-qualified-hot', 'lead-contacted-hot'];
  const isLeadStage = LEAD_STAGES.includes(deal?.stage);

  const handlePush = async (fnName) => {
    try {
      const result = await pushToArive(fnName, { dealId: deal.id });
      toast({ title: 'Success', description: `Pushed to Arive`, status: 'success', duration: 3000 });
      if (onDealUpdate) onDealUpdate();
    } catch (err) {
      toast({ title: 'Arive Error', description: err.message, status: 'error', duration: 5000 });
    }
  };

  const handleRefresh = async () => {
    await refresh();
    refetchFreshness(); // mirror's last_fetched_at just moved; update the badge
  };

  // Company-sync model: the panel shows for anyone who can see the deal
  // (page-level scoping) once the company sync user is configured. Personal
  // credentials remain a fallback gate when company sync isn't set up yet.
  if (credsLoading || accessLoading) return null;
  if (!companySyncConfigured && !(hasCredentials && isEnabled)) {
    return null;
  }

  return (
    <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" bg={bg} p={4}>
      <VStack align="stretch" spacing={3}>
        <HStack justify="space-between" flexWrap="wrap" gap={2} minW={0}>
          <HStack spacing={2} minW={0}>
            <Text fontWeight="bold" fontSize="sm">Arive LOS</Text>
            <AriveSyncBadge syncStatus={syncStatus} lastCheckedAt={lastCheckedAt} />
          </HStack>
          {isLinked && (
            <AriveDeepLink
              entityType={deal.ariveEntityType}
              ariveId={deal.ariveId}
              storedDeepLink={deal.ariveDeepLink}
              size="xs"
            />
          )}
        </HStack>

        {!isLinked && (
          <VStack spacing={2}>
            {isLeadStage ? (
              <Button
                size="sm" colorScheme="blue" leftIcon={<Icon as={FiUploadCloud} />}
                onClick={() => handlePush('pushLeadToArive')}
                isLoading={pushing} w="100%"
              >
                Create Lead in Arive
              </Button>
            ) : (
              <Button
                size="sm" colorScheme="green" leftIcon={<Icon as={FiUploadCloud} />}
                onClick={() => handlePush('pushLoanToArive')}
                isLoading={pushing} w="100%"
              >
                Create Loan in Arive
              </Button>
            )}
          </VStack>
        )}

        {isLinked && isLead && !isLeadStage && (
          <Button
            size="sm" colorScheme="purple" leftIcon={<Icon as={FiArrowRight} />}
            onClick={() => handlePush('convertLeadToLoan')}
            isLoading={pushing} w="100%"
          >
            Convert Lead to Loan in Arive
          </Button>
        )}

        {isLinked && (
          <>
            <AriveLoanSummary loanData={loanData} ariveId={deal.ariveId} entityType={deal.ariveEntityType} />

            <HStack justify="space-between" flexWrap="wrap" gap={2} minW={0}>
              {loanData.currentStage && (
                <Text fontSize="xs" color="gray.500" minW={0}>
                  Arive Stage: <Badge colorScheme="blue" fontSize="10px" whiteSpace="normal">{getAriveStageLabel(loanData.currentStage)}</Badge>
                </Text>
              )}
              <Button
                size="xs" variant="ghost" leftIcon={<Icon as={FiRefreshCw} />}
                flexShrink={0}
                onClick={handleRefresh} isLoading={refreshing}
              >
                {syncStatus === 'stale' ? 'Refresh (stale)' : 'Refresh'}
              </Button>
            </HStack>

            {isLoan && Object.keys(loanData).length > 3 && (
              <>
                <Divider />
                <Tabs index={tabIndex} onChange={setTabIndex} size="sm" variant="soft-rounded" colorScheme="blue">
                  <TabList flexWrap="wrap" gap={1}>
                    <Tab fontSize="xs">Dates</Tab>
                    <Tab fontSize="xs">Trackers</Tab>
                    <Tab fontSize="xs">Team</Tab>
                    <Tab fontSize="xs">Credit</Tab>
                    <Tab fontSize="xs">Financials</Tab>
                  </TabList>
                  <TabPanels mt={2}>
                    <TabPanel p={0}><AriveKeyDatesCard keyDates={loanData.keyDates} /></TabPanel>
                    <TabPanel p={0}><AriveTrackersCard trackers={loanData.loanTrackers} /></TabPanel>
                    <TabPanel p={0}><AriveLoanTeamCard loanData={loanData} /></TabPanel>
                    <TabPanel p={0}><AriveCreditCard ariveId={deal.ariveId} /></TabPanel>
                    <TabPanel p={0}><AriveFinancialCard loanData={loanData} /></TabPanel>
                  </TabPanels>
                </Tabs>
              </>
            )}
          </>
        )}
      </VStack>
    </Box>
  );
}
